// Обновленный CourseDisplay.jsx с адаптивностью
import React, { useState, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import axios from "axios";
import CourseNavigation from "./components/CouseNav";
import CoursePagination from "./components/CoursePagination";
import Videocard from "./video-card.jpg";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import "ldrs/react/LineSpinner.css";
import { Infinity } from "ldrs/react";
import "ldrs/react/Infinity.css";
import CourseOverview from "./CourseOverview";
import Comments from "./components/Comments";
import { FileMusic, Menu, FileText, X } from "lucide-react";
import {
  ExternalLink,
  Copy,
  Check,
  MoreHorizontal,
  Play,
  Users,
  Clock,
} from "lucide-react";

// Импортируем новые адаптивные компоненты
import CourseSidebar from "./components/CourseSidebar";
import PagesSidebar from "./components/PageSidebar";
import MobileCourseButtons from "./components/MobileCourseButtons";

const ImprovedQuizBlock = ({ block }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(block.quizLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Ошибка копирования:", err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: block.quizName,
          url: block.quizLink,
        });
      } catch (err) {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200 p-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-2 flex-1">
          <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Play className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              {block.quizName || "Интерактивный квиз"}
            </h3>
            <div className="flex items-center space-x-3 text-xs text-gray-500 mb-1">
              <span className="flex items-center">
                <Users className="w-3 h-3 mr-1" />
                Викторина
              </span>
              <span className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                Активна
              </span>
            </div>
            {block.quizLink && (
              <div className="text-xs text-gray-400 font-mono">
                {new URL(block.quizLink).hostname}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-1 ml-2">
          {block.quizLink && (
            <>
              <button
                onClick={handleCopyLink}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                title="Копировать ссылку"
                disabled={copied}
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
              <a
                href={block.quizLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <span className="mr-1">Открыть</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </>
          )}
          <button
            onClick={handleShare}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            title="Поделиться"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const getAccessiblePages = (pages) => {
  if (!pages) return {};

  const accessiblePages = {};
  Object.entries(pages).forEach(([pageId, page]) => {
    // Показываем только страницы с isAccessible: true
    if (page.isAccessible === true) {
      accessiblePages[pageId] = page;
    }
  });

  return accessiblePages;
};

// 2. 👈 НОВАЯ функция: Проверка доступа к модулю (есть ли хотя бы одна доступная страница)
const hasAccessiblePages = (module) => {
  if (!module || !module.pages) return false;

  return Object.values(module.pages).some((page) => page.isAccessible === true);
};

const BlockedPageMessage = () => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <svg
        className="w-8 h-8 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zM12 9a2 2 0 002-2V5a2 2 0 10-4 0v2a2 2 0 002 2z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-2">
      Урок заблокирован
    </h3>
    <p className="text-gray-500 text-sm">
      Этот урок станет доступен после выполнения предыдущих заданий или по
      решению преподавателя.
    </p>
  </div>
);

const CourseDisplay = () => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [activePage, setActivePage] = useState(null);
  const [showOverview, setShowOverview] = useState(true);
  const [showModulePages, setShowModulePages] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedPages, setExpandedPages] = useState({});
  const [accessDenied, setAccessDenied] = useState(false);

  const [pageBlocksLoading, setPageBlocksLoading] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);

  // Новые состояния для мобильных меню
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPagesMenuOpen, setIsPagesMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Определение размера экрана
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      if (!mobile) {
        setIsMobileMenuOpen(false);
        setIsPagesMenuOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (activeModule && activePage && course) {
      const currentPage = course.modules[activeModule]?.pages[activePage];
      if (currentPage && !currentPage.isAccessible) {
        setAccessDenied(true);
        // Перенаправляем на обзор модуля или курса
        setActivePage(null);
        setShowModulePages(true);
        setShowOverview(false);
      } else {
        setAccessDenied(false);
      }
    }
  }, [activeModule, activePage, course]);

  // Остальные ваши состояния для видеоплеера
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  const publicId = searchParams.get("publicId");
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);

  // Все ваши функции видеоплеера остаются без изменений
  const handlePlayPause = () => setPlaying(!playing);
  const handleProgress = (state) => setProgress(state.played * 100);
  const handleDuration = (d) => setDuration(d);
  const handleSeek = (e) => {
    const percent = parseFloat(e.target.value);
    setProgress(percent);
    playerRef.current?.seekTo(percent / 100);
  };
  const handleVolumeChange = (e) => setVolume(parseFloat(e.target.value));

  const [showControls, setShowControls] = useState(true);
  const [muted, setMuted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Все ваши функции для видео остаются без изменений
  const toggleMute = () => {
    setMuted(!muted);
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handlePictureInPicture = async () => {
    if (playerRef.current) {
      const videoElement = playerRef.current.getInternalPlayer();
      if (videoElement && videoElement.requestPictureInPicture) {
        try {
          await videoElement.requestPictureInPicture();
        } catch (error) {}
      }
    }
  };

  const skip = (seconds) => {
    const currentTime = playerRef.current?.getCurrentTime() || 0;
    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    playerRef.current?.seekTo(newTime);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const hideControlsTimer = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (playing) setShowControls(false);
    }, 3000);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    hideControlsTimer();
  };

  // Все ваши useEffect остаются без изменений
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (playing) {
      hideControlsTimer();
    } else {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
  }, [playing]);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!token || !publicId) {
        alert("Missing token or publicId in query params");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `https://us-central1-extent-protocol-a.cloudfunctions.net/getCourse?publicId=${publicId}&token=${token}`
        );
        setCourse(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [token, publicId]);

  useEffect(() => {
    if (activePage && activeModule && course) {
      setPageBlocksLoading(true);
      const timeout = setTimeout(() => {
        setPageBlocksLoading(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [activePage, activeModule]);

  useEffect(() => {
    const handlePopState = () => {
      if (activePage) {
        setActivePage(null);
        setShowModulePages(true);
        setShowOverview(false);
        setSidebarCollapsed(false);
      } else if (activeModule) {
        setActiveModule(null);
        setShowModulePages(false);
        setShowOverview(true);
        setSidebarCollapsed(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [activePage, activeModule]);

  const getUrl = (urlOrFile) => {
    if (!urlOrFile) return "";
    return urlOrFile.startsWith("http")
      ? urlOrFile
      : `https://firebasestorage.googleapis.com/v0/b/extent-protocol-a.firebasestorage.app/o/${encodeURIComponent(
          urlOrFile
        )}?alt=media`;
  };

  useEffect(() => {
    if (showModulePages && activeModule && course) {
      const pageIds = Object.keys(course.modules[activeModule].pages);
      const expanded = {};
      pageIds.forEach((id) => {
        expanded[id] = true;
      });
      setExpandedPages(expanded);
    }
  }, [showModulePages, activeModule, course]);

  const togglePageExpand = (pageId) => {
    setExpandedPages((prev) => ({
      ...prev,
      [pageId]: !prev[pageId],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white-50">
        <Infinity
          size="105"
          stroke="4"
          strokeLength="0.15"
          bgOpacity="0.1"
          speed="1.3"
          color="#006400"
        />
      </div>
    );
  }

  if (!course)
    return (
      <p className="text-center text-red-600 p-8 text-lg">
        Error loading course.
      </p>
    );

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-white-50">
      {/* Адаптивный сайдбар модулей */}
      <CourseSidebar
        courseName={course?.name}
        modules={
          // Фильтруем модули - показываем только те, где есть доступные страницы
          Object.fromEntries(
            Object.entries(course?.modules || {}).filter(([moduleId, module]) =>
              hasAccessiblePages(module)
            )
          )
        }
        activeModule={activeModule}
        onSelectModule={(moduleId) => {
          // Проверяем, есть ли доступные страницы в выбранном модуле
          const selectedModule = course?.modules[moduleId];
          if (hasAccessiblePages(selectedModule)) {
            setActiveModule(moduleId);
            setActivePage(null);
            setShowOverview(false);
            setShowModulePages(true);
            setSidebarCollapsed(false);
            window.history.pushState({}, "module-pages");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }}
        onShowOverview={() => {
          setShowOverview(true);
          setShowModulePages(false);
          setActiveModule(null);
          setActivePage(null);
          setSidebarCollapsed(false);
        }}
        sidebarCollapsed={sidebarCollapsed}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      {/* Адаптивный сайдбар уроков */}
      {activeModule && !showModulePages && (
        <PagesSidebar
          pages={
            // Показываем только доступные страницы
            getAccessiblePages(course?.modules[activeModule]?.pages || {})
          }
          activePage={activePage}
          onSelectPage={(pageId) => {
            // Дополнительная проверка доступа
            const selectedPage = course?.modules[activeModule]?.pages[pageId];
            if (selectedPage && selectedPage.isAccessible) {
              setActivePage(pageId);
              setShowOverview(false);
              setShowModulePages(false);
              setAccessDenied(false);
            } else {
              setAccessDenied(true);
            }
          }}
          onBackToModules={() => setSidebarCollapsed(false)}
          isPagesMenuOpen={isPagesMenuOpen}
          setIsPagesMenuOpen={setIsPagesMenuOpen}
        />
      )}

      {/* Основной контент */}
      <main
        className={`
        flex-1 overflow-auto
        ${isMobile ? "p-2" : "p-4 lg:p-6"}
        ${
          isMobile && (isMobileMenuOpen || isPagesMenuOpen)
            ? "overflow-hidden"
            : ""
        }
      `}
      >
        {/* Весь ваш существующий контент остается без изменений */}
        {showOverview && (
          <CourseOverview
            course={course}
            getUrl={getUrl}
            token={token}
            onSelectModule={(moduleId) => {
              // Проверяем доступность модуля перед переходом
              const selectedModule = course?.modules[moduleId];
              if (hasAccessiblePages(selectedModule)) {
                setActiveModule(moduleId);
                setActivePage(null);
                setShowOverview(false);
                setShowModulePages(true);
                setSidebarCollapsed(false);
                setLoadingPages(true);
                setTimeout(() => setLoadingPages(false), 1000);
                window.history.pushState({}, "module-pages");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          />
        )}

        {!showOverview && showModulePages && activeModule && (
          <div className="bg-white rounded-lg shadow-lg p-4 lg:p-6">
            {loadingPages ? (
              <div className="flex justify-center items-center h-64">
                <Infinity
                  size="80"
                  stroke="4"
                  strokeLength="0.15"
                  bgOpacity="0.1"
                  speed="1.3"
                  color="#16A34A"
                />
              </div>
            ) : (
              <>
                <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 border-b border-gray-200 pb-3">
                  {course.modules[activeModule].title}
                </h2>

                {/* 👈 НОВАЯ проверка: Есть ли доступные страницы */}
                {(() => {
                  const accessiblePages = getAccessiblePages(
                    course.modules[activeModule].pages
                  );
                  const accessibleCount = Object.keys(accessiblePages).length;

                  if (accessibleCount === 0) {
                    return (
                      <div className="text-center py-12">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg
                            className="w-10 h-10 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zM12 9a2 2 0 002-2V5a2 2 0 10-4 0v2a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">
                          Раздел временно недоступен
                        </h3>
                        <p className="text-gray-500 text-sm max-w-md mx-auto">
                          В этом разделе пока нет доступных уроков. Они появятся
                          по мере прохождения курса или по решению
                          преподавателя.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                      {Object.entries(accessiblePages).map(([pageId, page]) => (
                        <div
                          key={pageId}
                          className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer relative"
                          onClick={() => {
                            setActivePage(pageId);
                            setShowModulePages(false);
                            setShowOverview(false);
                            setSidebarCollapsed(true);
                            window.history.pushState({}, "page-view");
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          {/* 👈 НОВЫЙ индикатор доступности */}
                          <div className="absolute top-3 right-3 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                              />
                            </svg>
                            Доступен
                          </div>

                          <img
                            src={Videocard}
                            alt="Page Preview"
                            className="w-full h-32 lg:h-40 object-cover"
                          />
                          <div className="p-3 lg:p-4">
                            <h3 className="text-base lg:text-lg font-semibold text-gray-700 line-clamp-2">
                              {page.title}
                            </h3>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {!showOverview && activeModule && activePage && !showModulePages && (
          <div className="bg-white rounded-lg shadow-lg p-3 lg:p-6">
            <MobileCourseButtons
              showOverview={showOverview}
              activeModule={activeModule}
              activePage={activePage}
              showModulePages={showModulePages}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
              setIsPagesMenuOpen={setIsPagesMenuOpen}
              onShowOverview={() => {
                setShowOverview(true);
                setShowModulePages(false);
                setActiveModule(null);
                setActivePage(null);
                setSidebarCollapsed(false);
              }}
              onBackToModules={() => {
                setActivePage(null);
                setShowModulePages(true);
                setShowOverview(false);
                setSidebarCollapsed(false);
              }}
            />
            <CourseNavigation
              course={course}
              activeModule={activeModule}
              activePage={activePage}
              setActivePage={setActivePage}
              setActiveModule={setActiveModule}
              setShowOverview={setShowOverview}
              setSidebarCollapsed={setSidebarCollapsed}
              setShowModulePages={setShowModulePages}
            />

            <div className="mt-4 lg:mt-6">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 lg:mb-6 border-b border-gray-200 pb-3">
                {course.modules[activeModule].pages[activePage].title}
              </h2>

              {/* Весь ваш контент блоков остается без изменений */}
              {!pageBlocksLoading &&
                Object.entries(
                  course.modules[activeModule].pages[activePage].blocks
                ).map(([blockId, block]) => (
                  <div key={blockId} className="mb-6 lg:mb-8">
                    {/* Все ваши блоки контента остаются без изменений */}
                    {/* Только добавляем адаптивные классы где нужно */}

                    {block.type === "text" && (
                      <div
                        className="prose max-w-none text-sm lg:text-base"
                        dangerouslySetInnerHTML={{ __html: block.text }}
                      />
                    )}
                    {/* АДАПТИВНЫЙ БЛОК ИЗОБРАЖЕНИЙ */}
                    {block.type === "image" && (
                      <div className="w-full flex justify-center mb-6 lg:mb-8">
                        <img
                          src={getUrl(block.image)}
                          alt="Page Content"
                          className={`rounded-lg shadow-sm ${
                            block.size === "small"
                              ? "max-w-[200px] sm:max-w-xs lg:max-w-sm"
                              : block.size === "large"
                              ? "max-w-full w-full"
                              : "max-w-[280px] sm:max-w-sm lg:max-w-md"
                          } h-auto object-cover`}
                        />
                      </div>
                    )}

                    {/* АДАПТИВНЫЙ БЛОК ВИДЕО */}
               {block.type === "video" && (
  <div className="w-full max-w-4xl mx-auto mb-4 lg:mb-6 px-2 sm:px-0">
    {(() => {
      const videoUrl = getUrl(block.video || block.file);
      const isYouTube =
        videoUrl.includes("youtube.com") ||
        videoUrl.includes("youtu.be");
      const isVimeo = videoUrl.includes("vimeo.com");

      if (isYouTube || isVimeo) {
        let embedUrl = videoUrl;

        if (isYouTube) {
          const videoId = videoUrl.match(
            /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/
          )?.[1];
          embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
        } else if (isVimeo) {
          const videoId =
            videoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
          embedUrl = `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`;
        }

        return (
          <div className="relative w-full rounded-lg overflow-hidden shadow-lg">
            <div className="aspect-video">
              <iframe
                src={embedUrl}
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Видео контент"
              />
            </div>
          </div>
        );
      } else {
        return (
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-xl p-0.5 sm:p-1 shadow-2xl">
            <div className="relative rounded-lg sm:rounded-xl overflow-hidden bg-black">
              <div
                ref={containerRef}
                className="relative group cursor-pointer"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => playing && setShowControls(false)}
              >
                <ReactPlayer
                  ref={playerRef}
                  url={videoUrl}
                  playing={playing}
                  controls={false}
                  width="100%"
                  height="auto"
                  onProgress={handleProgress}
                  onDuration={handleDuration}
                  volume={muted ? 0 : volume}
                  playbackRate={playbackRate}
                  className="aspect-video"
                  config={{
                    file: {
                      attributes: {
                        controlsList: "nodownload",
                        onContextMenu: (e) => e.preventDefault(),
                      },
                    },
                  }}
                />

                {/* Play/Pause overlay */}
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                    showControls ? "opacity-100" : "opacity-0"
                  }`}
                  onClick={handlePlayPause}
                >
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 sm:p-4 lg:p-6 transform hover:scale-110 transition-all duration-300 shadow-xl border border-white/30">
                    {playing ? (
                      <svg
                        className="w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white ml-0.5 sm:ml-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Controls */}
                <div
                  className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-2 sm:p-3 lg:p-4 transition-all duration-300 ${
                    showControls
                      ? "translate-y-0 opacity-100"
                      : "translate-y-full opacity-0"
                  }`}
                >
                  {/* Progress bar */}
                                      <div className="mb-2 sm:mb-3 group">
                    <div className="relative">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={progress}
                        onChange={handleSeek}
                        className="w-full h-1 sm:h-1.5 lg:h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider-progress"
                        style={{
                          background: `linear-gradient(to right, #10b981 0%, #10b981 ${progress}%, rgba(255,255,255,0.2) ${progress}%, rgba(255,255,255,0.2) 100%)`,
                        }}
                      />
                      <div className="absolute -top-4 sm:-top-6 lg:-top-8 left-0 right-0 flex justify-between text-xs sm:text-sm text-white/80">
                        <span>{formatTime((progress * duration) / 100)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Main controls */}
                                      <div className="flex items-center justify-between gap-1 sm:gap-2">
                    {/* Left controls */}
                                          <div className="flex items-center gap-1 sm:gap-2">
                      {/* Play/Pause button */}
                      <button
                        onClick={handlePlayPause}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white p-2 sm:p-2.5 lg:p-3 rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg flex-shrink-0"
                      >
                        {playing ? (
                          <svg
                            className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                          </svg>
                        ) : (
                          <svg
                            className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>

                      {/* Volume controls - hidden on mobile */}
                      <div className="hidden sm:flex items-center gap-2 bg-white/10 rounded-full px-3 py-2 backdrop-blur-sm">
                        <button
                          onClick={toggleMute}
                          className="text-white hover:text-emerald-400 transition-colors flex-shrink-0"
                        >
                          {muted || volume === 0 ? (
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                            </svg>
                          )}
                        </button>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={muted ? 0 : volume}
                          onChange={handleVolumeChange}
                          className="w-16 lg:w-20 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer volume-slider"
                        />
                      </div>

                      {/* Mobile volume button */}
                      <button
                        onClick={toggleMute}
                        className="sm:hidden text-white hover:text-emerald-400 transition-colors p-2"
                      >
                        {muted || volume === 0 ? (
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                          </svg>
                        )}
                      </button>
                    </div>

                    {/* Right controls */}
                    <div className="flex items-center gap-1 sm:gap-2">
                      {/* Settings */}
                      <div className="relative">
                        <button
                          onClick={() => setShowSettings(!showSettings)}
                          className="bg-white/20 hover:bg-white/30 text-white p-2 sm:p-2.5 rounded-full transition-all duration-200 backdrop-blur-sm"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
                          </svg>
                        </button>

                        {showSettings && (
                          <div className="absolute bottom-full right-0 mb-2 bg-black/95 backdrop-blur-md rounded-lg p-3 min-w-[140px] sm:min-w-[160px] shadow-xl border border-white/20 z-50">
                            <div className="text-white text-sm">
                              <div className="mb-2 text-white/80">Скорость воспроизведения:</div>
                              <div className="grid grid-cols-3 gap-1">
                                {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                                  <button
                                    key={rate}
                                    onClick={() => setPlaybackRate(rate)}
                                    className={`px-2 py-1 text-xs rounded transition-all ${
                                      playbackRate === rate
                                        ? "bg-emerald-600 text-white"
                                        : "bg-white/20 hover:bg-white/30"
                                    }`}
                                  >
                                    {rate}x
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Picture in Picture - hidden on mobile */}
                      <button
                        onClick={handlePictureInPicture}
                        className="hidden md:block bg-white/20 hover:bg-white/30 text-white p-2 sm:p-2.5 rounded-full transition-all duration-200 backdrop-blur-sm"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 7h-8v6h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14z" />
                        </svg>
                      </button>

                      {/* Fullscreen */}
                      <button
                        onClick={handleFullscreen}
                        className="bg-white/20 hover:bg-white/30 text-white p-2 sm:p-2.5 rounded-full transition-all duration-200 backdrop-blur-sm"
                      >
                        {isFullscreen ? (
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Custom styles */}
            <style jsx>{`
              /* Progress slider */
              .slider-progress::-webkit-slider-thumb {
                appearance: none;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: #10b981;
                cursor: pointer;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
                transition: all 0.2s ease;
              }

              .slider-progress::-webkit-slider-thumb:hover {
                transform: scale(1.2);
                box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
              }

              .slider-progress::-moz-range-thumb {
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: #10b981;
                cursor: pointer;
                border: none;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
              }

              /* Volume slider */
              .volume-slider::-webkit-slider-thumb {
                appearance: none;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: #10b981;
                cursor: pointer;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                transition: all 0.2s ease;
              }

              .volume-slider::-webkit-slider-thumb:hover {
                transform: scale(1.1);
              }

              .volume-slider::-moz-range-thumb {
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: #10b981;
                cursor: pointer;
                border: none;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
              }

              /* Mobile optimizations */
              @media (max-width: 640px) {
                .slider-progress::-webkit-slider-thumb {
                  width: 16px;
                  height: 16px;
                }
                
                .slider-progress::-moz-range-thumb {
                  width: 16px;
                  height: 16px;
                }
              }

              /* Focus states for accessibility */
              .slider-progress:focus,
              .volume-slider:focus {
                outline: none;
              }

              .slider-progress:focus::-webkit-slider-thumb,
              .volume-slider:focus::-webkit-slider-thumb {
                box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.3);
              }

              /* Hide native range track */
              .slider-progress::-webkit-slider-track,
              .volume-slider::-webkit-slider-track {
                background: transparent;
              }

              .slider-progress::-moz-range-track,
              .volume-slider::-moz-range-track {
                background: transparent;
              }
            `}</style>
          </div>
        );
      }
    })()}
  </div>
)}

                    {block.type === "link" && (
                      <a
                        href={block.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-700 hover:text-green-900 underline font-medium text-sm lg:text-base"
                      >
                        {block.text}
                      </a>
                    )}

                    {block.type === "audio" && block.audio && (
                      <div className="w-full max-w-xl mx-auto bg-gray-50 p-3 lg:p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-3 lg:space-x-4">
                          <div className="flex-shrink-0 bg-yellow-100 rounded-full p-2">
                            <FileMusic className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" />
                          </div>
                          <div className="flex-1">
                            <audio
                              controls
                              preload="metadata"
                              className="w-full mt-1"
                            >
                              <source
                                src={getUrl(block.audio)}
                                type="audio/mpeg"
                              />
                              <source
                                src={getUrl(block.audio)}
                                type="audio/wav"
                              />
                              <source
                                src={getUrl(block.audio)}
                                type="audio/ogg"
                              />
                              Ваш браузер не поддерживает воспроизведение аудио.
                            </audio>
                          </div>
                        </div>
                      </div>
                    )}

{block.type === "file" &&
  block.file &&
  (() => {
    const fileUrl = getUrl(block.file);
    const getFileInfo = (filePath) => {
      if (!filePath)
        return { name: "", extension: "", isImage: false };
      const fileName = decodeURIComponent(
        filePath.split("/").pop().split("?")[0]
      );
      const extension =
        fileName.split(".").pop()?.toLowerCase() || "";
      const isImage = [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "svg",
      ].includes(extension);
      return { name: fileName, extension, isImage };
    };

    const getFileIcon = (extension) => {
      const iconClass = "w-4 h-4 lg:w-5 lg:h-5";
      if (
        [
          "jpg",
          "jpeg",
          "png",
          "gif",
          "webp",
          "svg",
        ].includes(extension)
      ) {
        return (
          <svg
            className={`${iconClass} text-blue-500`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        );
      }
      if (["pdf"].includes(extension)) {
        return (
          <svg
            className={`${iconClass} text-red-500`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        );
      }
      if (["doc", "docx"].includes(extension)) {
        return (
          <svg
            className={`${iconClass} text-blue-600`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        );
      }
      return (
        <svg
          className={`${iconClass} text-gray-500`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
          />
        </svg>
      );
    };

    const fileInfo = getFileInfo(fileUrl);

    return (
      <div className="w-full max-w-xl mx-auto bg-white p-2.5 lg:p-3 rounded-lg border border-gray-200 hover:shadow-sm transition-all">
        <div className="flex items-center space-x-2.5">
          <div className="flex-shrink-0">
            {fileInfo.isImage ? (
              <img
                src={fileUrl}
                alt={fileInfo.name}
                className="w-7 h-7 lg:w-8 lg:h-8 object-cover rounded border"
              />
            ) : (
              <div className="w-7 h-7 lg:w-8 lg:h-8 bg-gray-100 rounded border flex items-center justify-center">
                {getFileIcon(fileInfo.extension)}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 max-w-[200px] sm:max-w-[300px]">
            <p
              className="text-sm font-medium text-gray-900 truncate"
              title={fileInfo.name}
            >
              {fileInfo.name.length > 30 ? 
                `${fileInfo.name.substring(0, 30)}...` : 
                fileInfo.name
              }
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {fileInfo.extension.toUpperCase()} файл
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1.5 flex-shrink-0">
            <button
              onClick={() => window.open(fileUrl, "_blank")}
              className="text-xs lg:text-sm text-blue-600 hover:underline px-2 py-1 whitespace-nowrap"
              title="Открыть файл"
            >
              Открыть
            </button>
            <a
              href={fileUrl}
              download={fileInfo.name}
              className="text-xs lg:text-sm text-green-600 hover:underline px-2 py-1 whitespace-nowrap"
              title="Скачать файл"
            >
              Скачать
            </a>
          </div>
        </div>
      </div>
    );
  })()}

                    {block.type === "myQuizzes" && (
                      <ImprovedQuizBlock block={block} />
                    )}
                  </div>
                ))}
            </div>

            {pageBlocksLoading && (
              <div className="flex justify-center my-12 lg:my-16">
                <Infinity
                  size="80"
                  stroke="4"
                  strokeLength="0.15"
                  bgOpacity="0.1"
                  speed="1.3"
                  color="#16A34A"
                />
              </div>
            )}

            {!pageBlocksLoading && (
              <div className="mt-6 lg:mt-8 border-t border-gray-200 pt-4 lg:pt-6">
                <CoursePagination
                  course={course}
                  activeModule={activeModule}
                  activePage={activePage}
                  setActivePage={setActivePage}
                  setActiveModule={setActiveModule}
                  setShowOverview={setShowOverview}
                  setSidebarCollapsed={setSidebarCollapsed}
                  setShowModulePages={setShowModulePages}
                />
              </div>
            )}

            {!pageBlocksLoading && (
              <Comments
                publicId={publicId}
                token={token}
                moduleId={activeModule}
                pageId={activePage}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default CourseDisplay;
